<template>
    <div v-show="show" class="container p-0">
        <div class="row no-gutters">
            <div class="col-md-12 py-3 d-flex justify-content-between flex-wrap">
                <div>
                    <h4 v-if="isModified" class="text-warning">
                        <i class="fas fa-pen-square big"></i>
                        編集中の記事は公開されていません
                    </h4>
                    <div>
                        <a v-if="article.publish_at" :href="url" target="_blank">
                            公開記事を参照
                            <i class="fas fa-external-link-alt"></i>
                        </a>
                    </div>
                </div>
                <div v-if="!isOgp">
                    <label class="btn btn-outline-secondary btn-sm btn-radio" :class="{
                        active: article.rating_id === null,
                    }">
                        <input type="radio" v-model="article.rating_id" :value="null" class="d-none" />
                        <radio :value="article.rating_id === null"></radio>
                        無し
                    </label>
                    <label v-for="v in ratings" :key="v.id" class="btn btn-radio btn-sm"
                        :class="{
                            active: article.rating_id == v.id,
                            'btn-outline-warning': v.color == 'warning',
                            'btn-outline-danger': v.color == 'danger',
                            'btn-outline-dark': v.color == 'dark',
                        }">
                        <input type="radio" v-model="article.rating_id" :value="v.id" class="d-none" />
                        <radio :value="article.rating_id === v.id"></radio>
                        {{v.rating_name}}
                    </label>
                    <div class="text-muted small">
                        記事のレーティングを設定してください。
                    </div>
                </div>
            </div>
        </div>
        <div class="row no-gutters" v-if="!isOgp">
            <div class="col-md-12">
                <input type="text" class="form-control article-title shadow-none"
                    placeholder="記事タイトル"
                    @change="change"
                    v-model="article.title">
            </div>
        </div>
        <div class="row no-gutters" v-show="!isOgp">
            <div class="col-md-12">
                <editor
                    id="article-editor"
                    :content="$filters.jsonParse(article.edit_article)"
                    @saved="saved"
                    @change="isEdit = true"
                    :markdown="false"
                    ref="editor"
                    minHeight="150"
                    placeholder="本文を記述してください"
                    class="fullpage"></editor>
                <editor-storage :content="article.edit_article" :max="$store.state.max_article_size"></editor-storage>
            </div>
        </div>
        <div class="row no-gutters" v-if="isOgp">
            <div class="col-md-12 mb-4">
                <div class="input-group">
                    <input type="text" class="form-control" v-model="article.url"
                        placeholder="外部記事のURLを貼り付けてEnter" @keydown.enter="input">
                    <div class="input-group-append">
                        <button class="btn btn-outline-primary"
                            :class="{active: article.twitter}"
                            @click="article.twitter = !article.twitter">
                            <i class="far fa-check-square" v-if="article.twitter"></i>
                            <i class="far fa-square" v-else></i>
                            twitterタグ優先
                        </button>
                        <button class="btn btn-outline-primary" @click="reload" :disabled="!article.url">
                            <i class="fas fa-cloud-download-alt"></i>
                            読み込み
                        </button>
                    </div>
                </div>
                <div class="text-muted small">
                    OGP対応している外部記事にリンクを貼ることができます。
                    <span class="text-danger">
                        ご自身の所有していないコンテンツを、あたかもご自身の著作物のように掲載することはガイドライン違反となります。
                    </span>
                </div>
            </div>
        </div>
        <div class="row justify-content-center" v-if="isAvailable">
            <div class="col-md-4">
                <article-card
                    :noLink="true"
                    :article="article"></article-card>
            </div>
        </div>
        <div class="row no-gutters">
            <div class="col-md-12">
                <label class="caption">
                    タグ
                    <span :class="{'text-danger': article.tags.length >= 5}">({{article.tags.length}} / 5)</span>
                </label>
                <div>
                    <span class="badge badge-success badge-pill badge-tag" v-for="(v, i) in article.tags" :key="v.id">
                        {{v.tag_name}}
                        <button type="button" class="close" @click="removeTag(i)">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </span>
                </div>
                <div class="input-group">
                    <input type="text" class="form-control bg-white" style="height:41px" v-model="tag" placeholder="新規タグ (Enterで追加)" @keydown.enter="addTag" />
                    <div class="input-group-append">
                        <button type="button" class="btn btn-secondary" @click="addTag()">
                            <i class="fas fa-plus"></i>
                            追加 (Enter)
                        </button>
                    </div>
                </div>
            </div>
            <div class="col-md-12" v-if="mytags.length">
                <div class="mt-2">
                    <div>過去に使用したタグ</div>
                    <button type="button" class="btn btn-light btn-sm" v-for="t in mytags" :key="t.id" @click="appendTag(t)">
                        {{t.tag_name}}
                    </button>
                </div>
            </div>
        </div>
        <div class="row no-gutters">
            <div class="col-md-12">
                <label class="caption">公開先</label>
                <div class="text-muted small">
                    公開先が指定されていない場合、記事を公開してもどこにも表示されません。
                </div>
                <div class="mb-2">
                    <span class="badge badge-pill badge-primary badge-publish" v-for="(g, i) in article.genres" :key="g.id">
                        {{g.genre_name}}
                        <button type="button" class="close" aria-label="Close" @click="removeGenre(i)">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </span>
                    <button class="btn btn-light btn-sm" @click="showGenres()">
                        <i class="fas fa-plus"></i>
                        ジャンル...
                    </button>
                </div>
                <div class="mb-2">
                    <span class="badge badge-pill badge-primary badge-publish" v-for="(b, i) in article.books" :key="b.id">
                        {{b.title}}
                        <button type="button" class="close" aria-label="Close" @click="removeBooks(i)">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </span>
                    <button class="btn btn-light btn-sm" @click="showBooks()">
                        <i class="fas fa-plus"></i>
                        小説作品...
                    </button>
                </div>
                <div class="mb-2">
                    <span class="badge badge-pill badge-primary badge-publish" v-for="(s, i) in article.series" :key="s.id">
                        {{s.series_name}}
                        <button type="button" class="close" aria-label="Close" @click="removeSeries(i)">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </span>
                    <button class="btn btn-light btn-sm" @click="showSeries()">
                        <i class="fas fa-plus"></i>
                        シリーズ...
                    </button>
                </div>
            </div>
        </div>
        <div class="row no-gutters">
            <div class="col py-3 pr-2" v-if="!isOgp">
                <button class="btn btn-block"
                    :class="{
                        'btn-outline-secondary': !isEdit,
                        'btn-outline-danger': isEdit,
                    }"
                    @click="save(false)">
                    <i class="fas fa-save"></i>
                    下書き保存
                </button>
            </div>
            <div class="col py-3 pl-2">
                <button class="btn btn-outline-success btn-block" @click="publish()">
                    <i class="fas fa-upload"></i>
                    <span v-if="article.publish_at">更新</span>
                    <span v-else>公開</span>
                </button>
            </div>
            <div class="col py-3 pl-2" v-if="article.id && article.publish_at && !isOgp">
                <button class="btn btn-outline-danger btn-block" @click="unpublish()">
                    <i class="fas fa-upload"></i>
                    非公開
                </button>
            </div>
        </div>
        <div class="row no-gutters">
            <div class="col-md-12 remove-btn text-center">
                <button class="btn btn-outline-danger" @click="remove()" v-if="article.id">
                    <i class="fas fa-times"></i>
                    記事を削除
                </button>
                <button class="btn btn-outline-danger" @click="clear()" v-else-if="!isOgp && isEdit">
                    <i class="fas fa-times"></i>
                    編集をクリア
                </button>
            </div>
        </div>
        <article-genres
            @commit="commitGenres($event)"
            :article="article"></article-genres>
        <article-series
            @commit="commitSeries($event)"
            :article="article"></article-series>
        <article-books
            @commit="commitBooks($event)"
            :article="article"></article-books>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
    </div>
</template>

<style scoped lang="scss">
.container {
    max-width: 820px;
}
.caption {
    margin: 1rem 0 .5rem;
    font-weight: bold;
    font-size: 1rem;
}
.article-title {
    padding: 1rem 3rem;
    font-size: 2.0rem;
    border-bottom: 1px solid #aaa;
    border-radius: .5rem .5rem 0 0;
}
#article-editor {
    padding-top: 1rem;
    margin-top: 0;
}
.btn-radio {
    margin: .5em 1em .5em 0;
}
.badge-tag {
    padding: .7em 1em;
    font-size: 1rem;
    margin: 0 1em .5em 0;
    .close {
        margin: 0 0 0 .7em;
        font-size: 1rem;
    }
}
.badge-publish {
    font-size: .8rem;
    padding: .5rem .75rem;
}
.remove-btn {
    margin-top: 8rem;
}
.close {
    font-size: 1rem;
    color: white;
    text-shadow: none;
    padding: 0 0 .1rem .5rem;
}
@media screen and (max-width: 770px) {
    .article-title {
        padding: 1rem 0;
    }
}
</style>

<script>
import Dexie from "dexie";
import radio from "components/Radio";
import articleGenres from "./articleGenres";
import articleSeries from "./articleSeries";
import articleBooks from "./articleBooks";
import articleCard from "./articleCard";
let articleVue;
export default {
    components: {
        radio,
        articleGenres,
        articleBooks,
        articleSeries,
        articleCard,
    },
    data() {
        return {
            article: {
                title: null,
                edit_article: null,
                rating_id: null,
                site_name: null,
                url: null,
                eyecatch: null,
                preface: null,
                tags: [],
                genres: [],
                series: [],
                books: [],
            },
            ratings: [],
            mytags: [],
            show: false,
            tag: null,
            isEdit: false,
            isLoading: false,
            articledb: null,
        }
    },
    methods: {
        load() {
            this.isEdit = false;
            this.show = false;
            if (!this.$route.params.id) {
                // 新規記事
                this.articledb.articles.get({id: 0}).then(article => {
                    if (!this.isOgp && article) {
                        this.article = article;
                        this.isEdit = true;
                    } else {
                        this.clear();
                    }
                    this.show = true;
                    this.$refs.editor.refresh();
                }).catch(err => {
                    console.error(err);
                });
                return;
            }
            axios.get("/mypage/article/get", {params: {id: this.$route.params.id}}).then(res => {
                if (!res.data.edit_article && res.data.publish_article) {
                    res.data.edit_article = res.data.publish_article;
                }
                this.articledb.articles.get({id: res.data.id}).then(article => {
                    if (!this.isOgp && article) {
                        try {
                            const client_article = JSON.parse(article.edit_article);
                            const server_article = JSON.parse(res.data.edit_article);
                            if (client_article.time > server_article.time) {
                                res.data = article;
                                this.isEdit = true;
                            }
                        } catch (error) {}
                    }
                    this.article = res.data;
                    this.$refs.editor.refresh();
                    this.show = true;
                });
            }).catch(err => { alert.err(err) });
        },
        loadMytags() {
            axios.get("/mypage/article/mytags/get", {params: {except: this.$route.params.id}}).then(res => {
                this.mytags = res.data;
            }).catch(err => {
                alert.err(err.response);
            });
        },
        publish() {
            let action = "公開"
            let deny = false;
            let desc = "d-none";
            if (this.article.publish_at) {
                action = "更新";
                deny = true;
                desc = "";
            }
            Swal.fire({
                title: "記事" + action,
                html: `編集中の記事を${action}します。<br>
                <div class='text-left small p-3 ${desc}'>
                    公開済みの記事を更新すると、公開日時も更新されます。<br>
                    公開日時を更新したくない場合は、「記事の反映」をクリックしてください。
                </div>
                よろしいですか？`,
                icon: "warning",
                showCancelButton: true,
                showDenyButton: deny,
                confirmButtonText: action,
                denyButtonText: "記事の反映",
                cancelButtonText: "キャンセル",
                confirmButtonColor: "royalblue",
                cancelButtonColor: "gray",
                reverseButtons: true,
            }).then(confirm => {
                if (confirm.isConfirmed) {
                    this.article.publish_at = moment().format("YYYY-MM-DD HH:mm:ss");
                    this.save();
                } else if (confirm.isDenied) {
                    this.article.publish_at = moment().format("YYYY-MM-DD HH:mm:ss");
                    this.article.publish_only = true;
                    this.save();
                }
            });
        },
        unpublish() {
            Swal.fire({
                title: "記事非公開",
                text: "編集中の記事を非公開に戻します。\nよろしいですか？",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "非公開",
                cancelButtonText: "キャンセル",
                confirmButtonColor: "chocolate",
                cancelButtonColor: "gray",
                reverseButtons: true,
            }).then(confirm => {
                if (confirm.isConfirmed) {
                    this.article.publish_at = null;
                    this.save();
                }
            });
        },
        change() {
            this.isEdit = true;
            if (!this.isOgp) {
                this.articledb.articles.put(this.article);
            }
        },
        saved(ev) {
            this.article.edit_article = JSON.stringify(ev).replace(/<span[^>]*>/mug, "").replace(/<\/span>/mug, "");
            this.change();
        },
        async save(redirect = true) {
            let path = "/mypage/article/save";
            if (this.isOgp) {
                path = "/mypage/article/ogp/save";
            }
            axios.post(path, this.article).then(res => {
                this.isEdit = false;
                this.articledb.articles.delete(this.article.id);
                if (redirect) {
                    // 公開後はリダイレクト
                    this.$router.push("/mypage/articles?page=" + (this.$route.query.page || 1));
                } else if (!this.article.id) {
                    // 新規保存後は編集URLへ
                    this.$router.push("/mypage/article/" + res.data + "?page=1");
                    setTimeout(() => {
                        this.load();
                    });
                }
            }).catch(err => {
                this.article.publish_at = null;
                alert.err(err);
            });
        },
        addTag(ev = null) {
            if (ev && ev.keyCode !== 13) return;
            if (! this.article.tags || this.article.tags.length >= 5) return;
            if (! _.find(this.article.tags, {tag_name: this.tag})) {
                this.article.tags.push({
                    tag_name: this.tag
                });
                this.tag = null;
                this.change();
            }
        },
        appendTag(t) {
            if (! _.find(this.article.tags, {tag_name: t.tag_name})) {
                this.article.tags.push({
                    tag_name: t.tag_name
                });
                this.mytags = _.reject(this.mytags, {id: t.id});
                this.change();
            }
        },
        removeTag(i) {
            this.article.tags.splice(i, 1);
            this.change();
        },
        remove() {
            Swal.fire({
                title: "記事削除",
                html: `編集中の記事を削除します。
                <p class="text-danger">削除した記事はもとに戻せません。</p>
                よろしいですか？`,
                icon: "error",
                showCancelButton: true,
                confirmButtonText: "削除",
                cancelButtonText: "キャンセル",
                confirmButtonColor: "crimson",
                cancelButtonColor: "gray",
                reverseButtons: true,
            }).then(confirm => {
                if (confirm.isConfirmed) {
                    axios.post("/mypage/article/remove", {id: this.article.id}).then(res => {
                        alert.msg(res.data);
                        this.articledb.articles.delete(this.article.id);
                        this.isEdit = false;
                        this.$router.push("/mypage/articles");
                    }).catch(err => { alert.err(err.response) });
                }
            });
        },
        clear() {
            this.article = {
                id: 0,
                title: null,
                edit_article: null,
                rating_id: null,
                site_name: null,
                url: null,
                eyecatch: null,
                preface: null,
                tags: [],
                genres: [],
                series: [],
                books: [],
            };
            this.$refs.editor.refresh();
            if (!this.isOgp) {
                this.articledb.articles.delete(0);
            }
            this.isEdit = false;
        },
        showGenres() {
            $("#article-genres").modal("show");
        },
        commitGenres(ev) {
            this.article.genres = ev;
            this.change();
        },
        showSeries() {
            $("#article-series").modal("show");
        },
        commitSeries(ev) {
            this.article.series = ev;
            this.change();
        },
        showBooks() {
            $("#article-books").modal("show");
        },
        commitBooks(ev) {
            this.article.books = ev;
            this.change();
        },
        removeGenre(i) {
            this.article.genres.splice(i, 1);
            this.change();
        },
        removeBooks(i) {
            this.article.books.splice(i, 1);
            this.change();
        },
        removeSeries(i) {
            this.article.series.splice(i, 1);
            this.change();
        },
        reload() {
            this.isLoading = true;
            axios.post("/mypage/article/ogp/load", {
                url: this.article.url,
                twitter: this.article.twitter,
            }).then(res => {
                this.article.site_name = res.data.site_name ?? null;
                this.article.title = res.data.title;
                this.article.eyecatch = res.data.image ?? null;
                this.article.preface = res.data.description ?? null;
                this.isLoading = false;
            }).catch(err => {
                alert.err(err);
                this.article.site_name = null;
                this.article.title = null;
                this.article.eyecatch = null;
                this.article.preface = null;
                this.article.publish_at = null;
                this.isLoading = false;
            });
        },
        input(ev) {
            if (!ev.isComposing) {
                this.reload();
            }
        },
    },
    computed: {
        isModified() {
            if (!this.article.publish_at) {
                return false;
            }
            const diff = Math.abs(moment(this.article.publish_at).unix() - moment(this.article.updated_at).unix());
            return diff > 2;
        },
        url() {
            if (this.article.url) {
                return this.article.url;
            }
            return '//' + this.$store.state?.user?.subdomain + this.$store.state?.base_domain + "/author/article/" + this.article.id;
        },
        isAvailable() {
            if (this.isOgp && this.article.url && this.article.title) {
                return true;
            }
            return false;
        },
        isOgp() {
            if (this.$route.query.ogp) {
                return true;
            }
            return false;
        }
    },
    watch: {
        article: {
            handler: (n, o) => {
                // 新規記事保存ではなく、初期化ではなく、クリアではない
                if ((n.id === o.id) && typeof o.id !== "undefined" && n.edit_article !== null) {
                    articleVue.change();
                }
            },
            deep: true
        }
    },
    mounted() {
        articleVue = this;
        this.articledb = new Dexie("articledb");
        this.articledb.version(1).stores({articles: "id"});
        this.load();
        this.loadMytags();
        axios.get("/master/ratings").then(res => {
            this.ratings = res.data;
        }).catch(err => { alert.err(err.response) });
    },
    beforeRouteLeave(to, from, next) {
        if (this.isEdit && !this.isOgp && this.article.title && this.article.edit_article) {
            axios.post("/mypage/article/save", this.article).then(res => {
                this.articledb.articles.delete(this.article.id);
                next();
            }).catch(err => {
                console.error(err);
                next();
            });
        } else {
            next();
        }
    },
}
</script>